import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CodeClash, CodeFixState, CodeRow } from './code-fix.state';
import { SageErrorFixStore } from './sage-error-fix.store';

/** Item codes and names changed from the panel, checked as you type. */
@Component({
  selector: 'app-code-fix',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './code-fix.component.html',
  styleUrls: ['../onboarding-wizard/sage-wizard.shared.css', '../sage-mockup.shared.css', './fix-section.shared.css'],
})
export class CodeFixComponent {
  @Input({ required: true }) store!: SageErrorFixStore;
  @Input() idPrefix = '';

  readonly trackClash = (_: number, c: CodeClash) => c.code;
  readonly trackRow = (_: number, r: CodeRow) => r.itemId;

  get state(): CodeFixState {
    return this.store.codes;
  }

  get fieldLabel(): string {
    return this.state.field === 'code' ? 'Item code' : 'Name';
  }

  title(clash: CodeClash): string {
    if (clash.rows.length < 2) return clash.rows[0].name;
    const resolved = clash.rows.every((r) => !this.state.clashing(r));
    return `"${clash.code}" ${resolved ? 'was' : 'is'} used by ${clash.rows.length} items`;
  }

  sub(clash: CodeClash): string {
    if (clash.rows.length > 1) return clash.rows.every((r) => !this.state.clashing(r)) ? 'They differ now.' : 'Change any one of them.';
    const row = clash.rows[0];
    // 15 and 40 are Sage's limits for a good sent as a bundle; the others are Aivor's own.
    const limit = row.limit === 15 || row.limit === 40 ? `Sage allows up to ${row.limit} characters.` : `Up to ${row.limit} characters.`;
    return this.state.field === 'code' ? limit : `Item code ${row.code}. ${limit}`;
  }

  changed(row: CodeRow): boolean {
    return row.draft.trim() !== (this.state.field === 'code' ? row.code : row.name);
  }

  canSave(row: CodeRow): boolean {
    return this.changed(row) && !row.saving && !this.state.problem(row, row.draft);
  }

  /** What the field says under it: a problem, the clash it's in, or that it saved. */
  message(row: CodeRow): { tone: 'bad' | 'warn' | 'ok' | ''; text: string } {
    if (row.error) return { tone: 'bad', text: row.error };
    const problem = this.state.problem(row, row.draft);
    if (this.changed(row)) return problem ? { tone: 'bad', text: problem } : { tone: '', text: 'Press Save to use it.' };
    if (this.state.clashing(row) && problem) return { tone: 'warn', text: problem };
    if (row.saved) return { tone: 'ok', text: 'Saved. It goes to Sage on the next sync, or sync now.' };
    return { tone: '', text: '' };
  }

  suggestion(row: CodeRow): string | null {
    return this.state.field === 'code' && !this.changed(row) && this.state.clashing(row) ? this.state.freeCode(row) : null;
  }

  length(row: CodeRow): number {
    return row.draft.trim().length;
  }
}
